import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, LabelList, ResponsiveContainer, Cell
} from "recharts";
import { getCustomerZonePointsGraph } from "../actions/dashboardActions";
import CustomerZoneBarData from "./CustomerZoneBarData";
import NewWindow from "./NewWindow";

export default function CustomerZonePointsChart() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector((state) => state.user);
  const customerZonePointsGraphData = useSelector(
    (state) => state.dashboard?.customerZonePointsGraph || { items: [] }
  );

  const [showPopup, setShowPopup] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState(null);

  const getIsDark = () =>
    document.body.classList.contains("dark-mode") ||
    (!document.body.classList.contains("light-mode") &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const [isDark, setIsDark] = useState(getIsDark);

  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(getIsDark()));
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onMql = () => setIsDark(getIsDark());
    mql.addEventListener("change", onMql);
    return () => { observer.disconnect(); mql.removeEventListener("change", onMql); };
  }, []);

  useEffect(() => {
    if (user?.id) {
      dispatch(getCustomerZonePointsGraph(user.id));
    }
  }, [dispatch, user?.id]);

  const handleBarClick = (barData) => {
    if (barData.props?.id) {
      setSelectedZoneId(barData.props.id);
      setShowPopup(true);
    }
  };

  const renderTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      return (
        <div
          style={{
            background: isDark ? "#242b38" : "#fff",
            border: `1px solid ${isDark ? "#3a4256" : "#ccc"}`,
            color: isDark ? "#e6edf3" : "#333",
            padding: "8px",
            borderRadius: "6px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                backgroundColor: item.payload.color,
                marginRight: 6
              }}
            />
            <strong>{item.payload.title}</strong>
          </div>
          <div> {t("Actual")}: {item.payload.points} {t("pts")} ({t("Target")}: {item.payload.targetpoints} {t("pts")})</div>
        </div>
      );
    }
    return null;
  };

  const data = (customerZonePointsGraphData.items || []).map((z) => ({
    title: z.zoneName,
    targetpoints: z.targetPoints,
    points: z.points,
    performance: z.performance,
    color: `#${z.color}`,
    props: { id: z.id }
  }));
  const isMobile = window.innerWidth < 768;
  return (
    <Fragment>
      <div style={{ width: "100%", height: 400 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
                dataKey="title"
                interval={0}
                tick={({ x, y, payload }) => {
                    const maxCharsPerLine = 15;
                    let displayText = payload.value;

                    if (displayText.length > maxCharsPerLine) {
                        displayText = displayText.slice(0, maxCharsPerLine - 3) + "...";
                    }
                    return (
                        <g transform={`translate(${x},${y}) rotate(-60)`}>
                            <text
                                x={0}
                                y={0}
                                textAnchor="end"
                                fill="#666"
                                style={{ fontSize: 12 }}
                                title={payload.value}
                            >
                                {displayText}
                            </text>
                        </g>
                    );
                }}
            />
            <YAxis
              type = "number"
              domain={[0, 100]}
              label={{ value: t("Completion"), angle: -90, position: "insideLeft" }}
              allowDataOverflow={true}
            />
            <Tooltip
              content={renderTooltip}
              cursor={{ fill: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)" }}
            />

            <Bar
              dataKey="performance"
              name="Performance"
              isAnimationActive={true}
              onClick={handleBarClick}
            >
              <LabelList dataKey="performance" position="center" fill="#000"   style={{ fontSize: isMobile ? 8 : 14 }} formatter={(v) => `${v}%`} />
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

       {showPopup && (
         <>
             <style>
                 {`
                   .custom-modal {
                     padding-top: 40px;
                   }

                   .new-window-title {
                     margin-top: 0;
                     padding-right: 40px;
                   }
                 `}
             </style>

             <NewWindow
                 open={showPopup}
                 onClose={() => setShowPopup(false)}
                 title={t("Zone Details")}
                 fitContent={true}
                 customModalClass="custom-modal"
                 blockScroll={false}

             >
                 <CustomerZoneBarData zoneId={selectedZoneId} />
             </NewWindow>
         </>
 )}
    </Fragment>
  );
}
